import { Link } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, X } from 'lucide-react';
import CouponInput from '../components/CouponInput';
import AppIcon from '../components/AppIcon';
import { useCart } from '../context/CartContext';
import { useCoupon } from '../context/CouponContext';
import { useProducts } from '../context/ProductContext';
import { calculateOrderTotals } from '../utils/checkout';
import { formatAmount, formatPrice, STORE_CURRENCY } from '../utils/formatPrice';
import { getPrimaryImage, getProductUrl, getUnitPrice } from '../utils/product';

export default function Cart() {
  const { items, updateQuantity, removeFromCart, totalPrice } = useCart();
  const { getProductById } = useProducts();
  const { appliedCoupon } = useCoupon();
  const { subtotal, discountAmount, shipping, total } = calculateOrderTotals(totalPrice, appliedCoupon);

  if (items.length === 0) {
    return (
      <div className="container empty-state">
        <div className="empty-cart">
          <span className="empty-icon">
            <AppIcon icon={ShoppingCart} size={64} strokeWidth={1.5} />
          </span>
          <h2>Sepetiniz boş</h2>
          <p>Henüz sepetinize ürün eklemediniz.</p>
          <Link to="/shop" className="btn btn-primary">Alışverişe Başla</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container cart-page">
      <h1>Sepetim</h1>

      <div className="cart-layout">
        <div className="cart-items">
          {items.map(({ product, quantity }) => {
            const productSlug = getProductById(product.id)?.slug ?? product.slug;
            return (
            <div key={product.id} className="cart-item">
              <img src={getPrimaryImage(product)} alt={product.name} className="cart-item-image" />

              <div className="cart-item-info">
                <Link to={getProductUrl(productSlug)}>
                  <h3>{product.name}</h3>
                </Link>
                <span className="product-category">{product.category}</span>
                <p className="cart-item-price">{formatPrice(product)}</p>
              </div>

              <div className="quantity-controls">
                <button
                  onClick={() => updateQuantity(product.id, quantity - 1)}
                  aria-label="Azalt"
                >
                  −
                </button>
                <span>{quantity}</span>
                <button
                  onClick={() => updateQuantity(product.id, quantity + 1)}
                  aria-label="Artır"
                >
                  +
                </button>
              </div>

              <p className="cart-item-total">
                {formatAmount(getUnitPrice(product) * quantity, product.currency)}
              </p>

              <button
                className="remove-btn"
                onClick={() => removeFromCart(product.id)}
                aria-label="Kaldır"
              >
                <AppIcon icon={X} size={18} />
              </button>
            </div>
            );
          })}
        </div>

        <aside className="cart-summary">
          <h2>Sipariş Özeti</h2>

          <CouponInput subtotal={subtotal} currency={STORE_CURRENCY} />

          <div className="summary-row">
            <span>Ara Toplam</span>
            <span>{formatAmount(subtotal)}</span>
          </div>
          {discountAmount > 0 && (
            <div className="summary-row summary-discount">
              <span>İndirim{appliedCoupon ? ` (${appliedCoupon.code})` : ''}</span>
              <span>−{formatAmount(discountAmount)}</span>
            </div>
          )}
          <div className="summary-row">
            <span>Kargo</span>
            <span>{shipping === 0 ? 'Ücretsiz' : formatAmount(shipping)}</span>
          </div>

          <div className="summary-row summary-total">
            <span>Toplam</span>
            <span>{formatAmount(total)}</span>
          </div>

          {subtotal < 350 && (
            <p className="free-shipping-note">
              {formatAmount(350 - subtotal)} daha ekleyin, ücretsiz kargo kazanın!
            </p>
          )}

          <Link to="/checkout" className="btn btn-primary btn-lg btn-block">
            Ödemeye Geç
          </Link>

          <Link to="/shop" className="continue-shopping">
            <ArrowLeft size={16} aria-hidden />
            Alışverişe Devam Et
          </Link>
        </aside>
      </div>
    </div>
  );
}
